import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { iniciarSessaoFunil, rastrearEventoFunil, enviarFilaFunil } from '../../services/rastreamentoFunil';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { obterIdTenent, obterLimiteVeiculos, ehPlanoGratuito, estaEmPeriodoDeTeste, expiraEm, obterEmailUsuario, obterTipoPlano, obterIntegradoLocaSmartCheck } from '../../services/seguranca';
import { parseDataBRParaDate } from '../../services/utilitarios';
import { AxiosError } from 'axios';
import { ApiError, PLAN_OPTIONS, PlanType, calcularPrecoLocaSmartCheck, getAdjustedDiscount, getInstallmentOptions, isInstallmentEligible } from '../../services/tipos';
import CheckoutForm from './checkout-form';
import CheckoutSummary from './checkout-summary';
import WizardStepper from './wizard/WizardStepper';
import StepPlan from './wizard/StepPlan';
import StepAddons from './wizard/StepAddons';
import StepPayment from './wizard/StepPayment';
import CheckoutAssistant from './assistant/CheckoutAssistant';
import ModalOnboarding from '../locasmartcheck/ModalOnboarding';
import { FaArrowLeft } from 'react-icons/fa';
import { styled } from 'styled-components';
import { Loader2 } from 'lucide-react';
import { ExpansaoSimulacao } from './checkout-summary';

type PaymentMethod = 'pix' | 'creditcard';

type PaymentStorageState = {
  paymentMethod: PaymentMethod;
  pedidoId: string;
  pixData?: {
    qrCode: string;
    qrCodeImage: string;
    paymentUrl: string;
    pixKey: string;
  };
  timestamp: number;
  status: 'processing' | 'completed' | 'failed';
};

const TopBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: -2.5rem;
  position: relative;
  z-index: 10;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(5px);
  border: 1px solid #1F467E;
  color: #1F467E;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 30px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  margin-left: 0;

  &:hover {
    background: #1F467E;
    color: white;
    transform: translateX(-3px);
  }
`;

const CheckoutPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const tipoPlano = (query.get('tipo') as 'PRO' | 'AVANCADO') || 'PRO';
  const acao = query.get('acao') as 'upgrade' | 'expandir' | null;

  const cicloUrl = query.get('ciclo')?.toUpperCase();
  const mapCiclo: Record<string, PlanType> = {
    'MENSAL': 'MONTHLY', 'MONTHLY': 'MONTHLY',
    'TRIMESTRAL': 'QUARTERLY', 'QUARTERLY': 'QUARTERLY',
    'SEMESTRAL': 'SEMIANNUAL', 'SEMIANNUAL': 'SEMIANNUAL',
    'ANUAL': 'ANNUAL', 'ANNUAL': 'ANNUAL'
  };
  const cicloInicial = mapCiclo[cicloUrl || ''] || 'MONTHLY';

  const [currentPlanType, setCurrentPlanType] = useState<'PRO' | 'AVANCADO'>('PRO');
  
  const token = useAppSelector((state) => state.token);
  const isCadastroIncompleto = useAppSelector((state) => state.isCadastroIncompleto);

  const isUpgrade = acao === 'upgrade';
  const isExpansion = acao === 'expandir';

  const [selectedPlan, setSelectedPlan] = useState<PlanType>(cicloInicial);

  const isMonthly = selectedPlan === 'MONTHLY';
  // PIX indisponível para mensal (exceto upgrade/expansão que é cobrança avulsa)
  const pixIndisponivel = isMonthly && !isUpgrade && !isExpansion;

  const [hasPendingInvoice, setHasPendingInvoice] = useState(false);
  const [pixData, setPixData] = useState<{
    qrCode: string;
    qrCodeImage: string;
    paymentUrl: string;
    pixKey: string;
  } | null>(null);

  const currentVehicleLimit = Number(obterLimiteVeiculos(token));
  const isAdvanced = tipoPlano === 'AVANCADO';
  const basePlanLimit = isAdvanced ? 10 : 5;

  const effectiveMinLimit = (isExpansion || isUpgrade) ? currentVehicleLimit : basePlanLimit;

  const [vehicleCount, setVehicleCount] = useState(() => {
    if (isExpansion || isUpgrade) return currentVehicleLimit;
    return Math.max(currentVehicleLimit, basePlanLimit);
  });

  // Expansão: o valor exibido e cobrado é o que o SERVIDOR simula (acréscimo de tabela × dias restantes ÷ 30,
  // sem desconto de ciclo). A tela não calcula por conta própria.
  const [expansaoSimulacao, setExpansaoSimulacao] = useState<ExpansaoSimulacao | null>(null);

  const dataExp = parseDataBRParaDate(expiraEm(token));
  const planoExpirado = dataExp ? dataExp < new Date() : false;
  const temPlanoAtivo = !ehPlanoGratuito(token) && !estaEmPeriodoDeTeste(token) && !planoExpirado;
  const isLocaSmartCheckAtivo = obterIntegradoLocaSmartCheck(token);
  // Na expansão o Check entra só se a empresa já o usa (o servidor cobra pelo cadastro, não pela URL).
  const [incluirLocaSmartCheck, setIncluirLocaSmartCheck] = useState(query.get('locasmartcheck') === '1' && !isLocaSmartCheckAtivo && !isExpansion);
  const [showLocaSmartCheckOnboarding, setShowLocaSmartCheckOnboarding] = useState(false);
  const [installmentCount, setInstallmentCount] = useState(1);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    (isExpansion || isUpgrade) ? 'pix' : (isMonthly ? 'creditcard' : 'pix')
  );

  useEffect(() => {
    if (pixIndisponivel) {
      setPaymentMethod('creditcard');
    }
  }, [pixIndisponivel]);

  useEffect(() => {
    setInstallmentCount(1);
  }, [selectedPlan, paymentMethod]);

  useEffect(() => {
    const carregarTipoPlano = async () => {
      try {
        const plano = await obterTipoPlano(token);
        setCurrentPlanType((plano as 'PRO' | 'AVANCADO') || 'PRO');
      } catch (error) {
        console.error('Erro ao obter tipo do plano:', error);
        setCurrentPlanType('PRO');
      }
    };
  
    if (token) {
      carregarTipoPlano();
    }
  }, [token]);

  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(false);
  const [countdown, setCountdown] = useState(300);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({
    cardName: '',
    cardNumber: '',
    expiration: '',
    cvv: '',
    cpf: '',
    postalCode: '',
    addressNumber: '',
    phone: ''
  });
  const [cupomUsado, setCupomUsado] = useState<string>("");
  const [indicadorNome, setIndicadorNome] = useState<string>("");
  const [autoCupomTentado, setAutoCupomTentado] = useState(false);
  const [activePaymentId, setActivePaymentId] = useState<string | null>(null);
  const dataExpiracao = temPlanoAtivo ? parseDataBRParaDate(expiraEm(token)) : null;
  const [isCheckingPendingInvoice, setIsCheckingPendingInvoice] = useState(temPlanoAtivo ? false : true);
  const estaAumentando = temPlanoAtivo && (acao === 'upgrade' || acao === 'expandir');

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const isWizardFlow = !isExpansion && !isUpgrade;

  const snapshotFunil = () => ({
    tipo: tipoPlano,
    acao,
    ciclo: selectedPlan,
    veiculos: vehicleCount,
    locasmartcheck: incluirLocaSmartCheck,
    pagamento: paymentMethod,
    parcelas: installmentCount,
    etapa: currentStep,
  });

  useEffect(() => {
    iniciarSessaoFunil();
    rastrearEventoFunil('iniciou_checkout', snapshotFunil());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aoSelecionarCiclo = (plano: PlanType) => {
    rastrearEventoFunil('selecionou_ciclo', { ...snapshotFunil(), ciclo: plano });
    setSelectedPlan(plano);
  };

  const aoAlterarVeiculos = (quantidade: number) => {
    rastrearEventoFunil('alterou_veiculos', { ...snapshotFunil(), veiculos: quantidade }, { coalescer: true });
    setVehicleCount(quantidade);
  };

  const aoAlterarLocaSmartCheck = (incluir: boolean) => {
    rastrearEventoFunil(incluir ? 'marcou_locasmartcheck' : 'desmarcou_locasmartcheck', snapshotFunil());
    setIncluirLocaSmartCheck(incluir);
  };

  const aoSelecionarPagamento = (metodo: PaymentMethod) => {
    rastrearEventoFunil('selecionou_pagamento', { ...snapshotFunil(), pagamento: metodo });
    setPaymentMethod(metodo);
  };

  const aoAlterarParcelamento = (parcelas: number) => {
    rastrearEventoFunil('alterou_parcelamento', { ...snapshotFunil(), parcelas }, { coalescer: true });
    setInstallmentCount(parcelas);
  };

  const handleNextStep = () => {
    rastrearEventoFunil('avancou_etapa', snapshotFunil());
    setCurrentStep((prev) => {
      const next = Math.min(prev + 1, 3);
      // Pula step 2 (addons) se LocaSmartCheck já está ativo
      if (next === 2 && isLocaSmartCheckAtivo) return 3;
      return next;
    });
  };
  const handlePrevStep = () => setCurrentStep((prev) => {
    const prevStep = Math.max(prev - 1, 1);
    // Pula step 2 (addons) se LocaSmartCheck já está ativo
    if (prevStep === 2 && isLocaSmartCheckAtivo) return 1;
    return prevStep;
  });

  const handleAddonsNext = () => handleNextStep();
  const handleAddonsSkip = () => {
    rastrearEventoFunil('pulou_addons', snapshotFunil());
    setIncluirLocaSmartCheck(false);
    handleNextStep();
  };

  const calcularPrecoMensalParaStep = (count: number, plano: string) => {
    return calcularPrecoMensalSemDesconto(count, plano);
  };

  useEffect(() => {
    if (vehicleCount < effectiveMinLimit) {
      setVehicleCount(effectiveMinLimit);
    }
  }, [effectiveMinLimit, vehicleCount]);

  useEffect(() => {
    if (!isExpansion) { setExpansaoSimulacao(null); return; }
    let cancelado = false;
    (async () => {
        try {
            const resp = await api.post('/api/payment/expansao/simular', {
                IdEmpresa: obterIdTenent(token),
                QuantidadeDeVeiculos: vehicleCount
            });
            if (!cancelado) {
                setExpansaoSimulacao({
                    ehLocaSmartTrack: !!resp.data.ehLocaSmartTrack,
                    valorProRata: Number(resp.data.valorProRata) || 0,
                    valorPorPlacaMensal: Number(resp.data.valorPorPlacaMensal) || 0,
                    atualValorMensal: Number(resp.data.atualValorMensal) || 0,
                    novoValorMensal: Number(resp.data.novoValorMensal) || 0,
                    diasRestantes: Number(resp.data.diasRestantes) || 0,
                    limiteAtual: Number(resp.data.limiteAtual) || 0,
                    blocosAdicionais: Number(resp.data.blocosAdicionais) || 0,
                    valorBloco: Number(resp.data.valorBloco) || 0,
                    acrescimoMensalPlano: Number(resp.data.acrescimoMensalPlano) || 0,
                    placasAdicionais: Number(resp.data.placasAdicionais) || 0,
                    acrescimoMensalCheck: Number(resp.data.acrescimoMensalCheck) || 0,
                    acrescimoMensal: Number(resp.data.acrescimoMensal) || 0,
                    motivo: resp.data.motivo || null
                });
            }
        } catch {
            if (!cancelado) setExpansaoSimulacao(null);
        }
    })();
    return () => { cancelado = true; };
  }, [isExpansion, vehicleCount, token]);

  // Upgrade: o valor exibido e enviado é o que o SERVIDOR calcula (o mesmo que será cobrado). O cálculo
  // local divergia por 1 dia (relógio do navegador x data civil do servidor) e, com poucos dias restantes,
  // passava da tolerância: a cobrança era recusada e recarregar a página não resolvia.
  const [valorUpgradeServidor, setValorUpgradeServidor] = useState<number | null>(null);
  const [mensaisUpgradeServidor, setMensaisUpgradeServidor] = useState<{ atual: number; novo: number } | null>(null);
  useEffect(() => {
    if (!isUpgrade) { setValorUpgradeServidor(null); setMensaisUpgradeServidor(null); return; }
    let cancelado = false;
    (async () => {
        try {
            const resp = await api.post('/api/payment/upgradePlano/simular', {
                IdEmpresa: obterIdTenent(token),
                QuantidadeDeVeiculos: vehicleCount,
                TipoPlano: tipoPlano,
                Ciclo: PLAN_OPTIONS.find(p => p.id === selectedPlan)?.backendValue || 'MONTHLY',
                CodigoCupom: cupomUsado || null
            });
            if (!cancelado) {
                setValorUpgradeServidor(typeof resp.data?.valor === 'number' ? resp.data.valor : null);
                setMensaisUpgradeServidor(typeof resp.data?.atualValorMensal === 'number' && typeof resp.data?.novoValorMensal === 'number'
                    ? { atual: resp.data.atualValorMensal, novo: resp.data.novoValorMensal } : null);
            }
        } catch {
            // Sem cotação do servidor o checkout segue com o cálculo local (comportamento anterior).
            if (!cancelado) { setValorUpgradeServidor(null); setMensaisUpgradeServidor(null); }
        }
    })();
    return () => { cancelado = true; };
  }, [isUpgrade, vehicleCount, tipoPlano, selectedPlan, cupomUsado, token]);


  const obterPrecosPlano = (plano: string) => {
    const isAdvanced = plano?.toUpperCase() === 'AVANCADO' || plano?.toUpperCase() === 'AVANÇADO';
    // Tabela única: a antiga (49,90 / 79,90) não vale mais para nenhuma adesão, expansão ou troca de plano.
    // Bloco adicional de 5 placas desde 19/10/2026: PRO 12,90 / AVANÇADO 19,90.
    return {
      base: isAdvanced ? 109.9 : 79.9,
      additional: isAdvanced ? 19.9 : 12.9
    };
  };

  const calcularPrecoMensalSemDesconto = (count: number, plano: string) => {
    const { base, additional } = obterPrecosPlano(plano);
    const isAdvanced = plano?.toUpperCase() === 'AVANCADO' || plano?.toUpperCase() === 'AVANÇADO';
    const limitBase = isAdvanced ? 10 : 5;
    if (count <= limitBase) return base;
    const additionalVehicles = count - limitBase;
    const additionalGroups = Math.ceil(additionalVehicles / 5);
    return base + Math.max(0, additionalGroups) * additional;
  };
  
  const selectedPlanOption = PLAN_OPTIONS.find(p => p.id === selectedPlan);
  const cycleMonths = selectedPlanOption?.cycleMonths || 1;
  const parcelado = installmentCount > 1;
  const planDiscount = getAdjustedDiscount(selectedPlan, parcelado);

  const precoAtualMensalSemDesconto = calcularPrecoMensalSemDesconto(currentVehicleLimit, currentPlanType);
  const precoAtualMensal = precoAtualMensalSemDesconto * (1 - planDiscount);

  const precoNovoMensalSemDesconto = calcularPrecoMensalSemDesconto(vehicleCount, tipoPlano);
  const precoNovoMensal = precoNovoMensalSemDesconto * (1 - planDiscount);

  const precoTotalPlano = precoNovoMensal * cycleMonths;
  const precoTotalBase = precoNovoMensalSemDesconto * cycleMonths;
  const descontoTotalPlano = precoTotalBase - precoTotalPlano;

  // LocaSmartCheck addon — agora recebe o MESMO desconto de ciclo do plano
  const locaSmartCheckAtivo = incluirLocaSmartCheck || isLocaSmartCheckAtivo;
  const precoLocaSmartCheckMensal = locaSmartCheckAtivo ? calcularPrecoLocaSmartCheck(vehicleCount) * (1 - planDiscount) : 0;
  const precoLocaSmartCheckCiclo = precoLocaSmartCheckMensal * cycleMonths;
  const precoLocaSmartCheckMensalAtual = (isLocaSmartCheckAtivo) ? calcularPrecoLocaSmartCheck(currentVehicleLimit) * (1 - planDiscount) : 0;

  const calcularDiasRestantes = () => {
    if (!dataExpiracao) return 0;
    const hoje = new Date();
    const diffTime = Math.max(0, dataExpiracao.getTime() - hoje.getTime());
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  // Dias do servidor na expansão (data civil BRT, limitados ao ciclo); o relógio do navegador divergia por 1 dia.
  const diasRestantes = isExpansion && expansaoSimulacao ? expansaoSimulacao.diasRestantes : calcularDiasRestantes();

  const calcularDiferencaProporcional = () => {
    if (!temPlanoAtivo || diasRestantes <= 0) return precoTotalPlano;

    // Calcula diferença mensal do plano base (novo - atual)
    let diferencaMensal = precoNovoMensal - precoAtualMensal;

    // Inclui diferença do LocaSmartCheck se ativo (mais placas = preço maior)
    if (locaSmartCheckAtivo) {
      diferencaMensal += precoLocaSmartCheckMensal - precoLocaSmartCheckMensalAtual;
    }

    if (diferencaMensal <= 0) return 0;

    // Valor por dia da diferença
    const valorDiario = diferencaMensal / 30;
    const diferencaProporcional = valorDiario * diasRestantes;

    // Mínimo de 5 reais para gerar cobrança
    return diferencaProporcional < 5 ? 5 : diferencaProporcional;
  };

  const diferencaProporcional = calcularDiferencaProporcional();
  // Se for upgrade/expansão, o totalPrice é só a diferença proporcional hoje
  const totalPrice = temPlanoAtivo ? diferencaProporcional : (precoTotalPlano + precoLocaSmartCheckCiclo);
  const finalPrice = isExpansion
    ? (expansaoSimulacao?.valorProRata ?? 0)
    : (isUpgrade && valorUpgradeServidor !== null && valorUpgradeServidor > 0)
      ? valorUpgradeServidor
      : Math.max(0, totalPrice - discount);
  const showInstallments = paymentMethod === 'creditcard' && isInstallmentEligible(selectedPlan) && finalPrice > 500;
  const installmentOptions = showInstallments ? getInstallmentOptions(finalPrice) : [];

  const basePrice = obterPrecosPlano(tipoPlano).base;
  const precoAtual = precoAtualMensal;
  const mensaisServidor = isExpansion && expansaoSimulacao?.atualValorMensal && expansaoSimulacao?.novoValorMensal
    ? { atual: expansaoSimulacao.atualValorMensal, novo: expansaoSimulacao.novoValorMensal }
    : isUpgrade ? mensaisUpgradeServidor : null;

  const monitorPaymentStatus = async (pedidoId: string) => {
    const startTime = Date.now();
    const timeout = 300000; // 5 minutos

    while (Date.now() - startTime < timeout) {
      try {
        const response = await api.get(`pedido/${pedidoId}`);
        const { status } = response.data;

        if (status === 'Aprovado' || status === 'Concluído') {
          rastrearEventoFunil('checkout_concluido', snapshotFunil());
          enviarFilaFunil();
          toast.success('Pagamento confirmado com sucesso!');
          await new Promise(resolve => setTimeout(resolve, 2000));
          if (incluirLocaSmartCheck) {
            clearPaymentState();
            setShowLocaSmartCheckOnboarding(true);
            return;
          }
          handleLogout();
          return;
        } else if (status === 'Rejeitado') {
          rastrearEventoFunil('checkout_falhou', { ...snapshotFunil(), motivo: 'pagamento_recusado' });
          enviarFilaFunil();
          toast.error('Pagamento recusado');
          setIsSubmitting(false);
          setIsButtonsDisabled(false);
          clearPaymentState();
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 3000));
      } catch (error) {
        console.error('Erro ao verificar status:', error);
        break;
      }
    }

    const savedPayment = localStorage.getItem('activePayment');
    if (savedPayment) {
      const paymentState: PaymentStorageState = JSON.parse(savedPayment);
      savePaymentState({
        ...paymentState,
        status: 'failed'
      });
    }

    toast.info('O pagamento ainda está em processamento. Você receberá um e-mail com a confirmação.');
    setIsSubmitting(false);
    setIsButtonsDisabled(false);
  };

  useEffect(() => {
    if (isCadastroIncompleto) {
      toast.error('Por favor, complete seu cadastro antes de prosseguir com o pagamento');
      navigate.push('/minha-conta/?complete-cadastro');
    }
  }, [isCadastroIncompleto, navigate]);

  useEffect(() => {
    const checkPendingInvoice = async () => {
      try {
        setIsCheckingPendingInvoice(true);
        const response = await api.get(`/api/payment/subscription/pending/${obterIdTenent(token)}`);

        if (response.data) {
          setHasPendingInvoice(true);
          toast.warn('Você tem uma fatura pendente para este mês. Por favor, regularize seu pagamento.', {
            autoClose: false,
            toastId: 'pending-invoice'
          });

          const qtdVeiculo = Number(response.data.qtdVeiculo);
          setVehicleCount(qtdVeiculo || 0);

          const pixData = {
            qrCode: response.data.pixQrCode,
            qrCodeImage: response.data.pixQrCodeImageUrl,
            paymentUrl: response.data.paymentUrl,
            pixKey: response.data.pixKey
          };

          setPixData(pixData);
          setPaymentMethod("pix");
          savePaymentState({
            paymentMethod: 'pix',
            pedidoId: response.data.pedidoId,
            pixData,
            timestamp: Date.now(),
            status: 'processing'
          });

          if (pixData.pixKey) {
            toast.success('QR Code PIX carregado!');
          }
        }
      } catch (error) {
        if ((error as AxiosError).response?.status !== 404) {
          setIsCheckingPendingInvoice(false);
          console.error('Erro ao verificar fatura pendente:', error);
        }
      } finally {
        setIsCheckingPendingInvoice(false);
      }
    };

    if (!temPlanoAtivo) {
      checkPendingInvoice();
    }
  }, [token]);

  const savePaymentState = (state: PaymentStorageState) => {
    localStorage.setItem('activePayment', JSON.stringify(state));
    setActivePaymentId(state.pedidoId);
  };

  const clearPaymentState = () => {
    localStorage.removeItem('activePayment');
    setActivePaymentId(null);
    toast.dismiss('processing-payment');
  };

  function tratarErro(error: any) {
    const mensagem = error.response?.data.Mensagem != undefined ? error.response?.data.Mensagem : error.response?.data.message ? error.response?.data.message : 'Erro desconhecido ao processar pagamento.';
    toast.error('Não foi possível continuar: ' + mensagem);
    setIsSubmitting(false);
    clearPaymentState();
  }

  const handleCouponApply = async (code: string, silent = false) => {
    if (!code.trim()) {
      if (!silent) toast.info('Informe um código de cupom válido');
      return;
    }

    // O cupom incide sobre o plano + LocaSmartCheck (precoLocaSmartCheckCiclo é 0 quando não incluído)
    const baseParaCupom = temPlanoAtivo ? totalPrice : (precoTotalPlano + precoLocaSmartCheckCiclo);
    if (!silent) setIsSubmitting(true);
    try {
      const response = await api.post(`cupom/calcularDesconto/${code}/${baseParaCupom}/${obterIdTenent(token)}`);
      const valorComDesconto = response.data;
      const valorDesconto = baseParaCupom - valorComDesconto;
      if (valorDesconto <= 0) {
        // Recalculo silencioso (base mudou): se o cupom deixou de gerar desconto, zera o desconto
        // antigo — senão ele persiste e vai subfaturado como Valor no checkout.
        if (silent) {
          setDiscount(0);
          setCupomUsado("");
        } else {
          rastrearEventoFunil('aplicou_cupom', { ...snapshotFunil(), cupom: code, sucesso: false });
          toast.info("Informe um código de cupom válido");
          setIsSubmitting(false);
        }
        return;
      }
      setDiscount(valorDesconto);
      setCupomUsado(code);
      if (!silent) {
        rastrearEventoFunil('aplicou_cupom', { ...snapshotFunil(), cupom: code, desconto: valorDesconto, sucesso: true });
        toast.success(`Cupom aplicado! Desconto de ${valorDesconto.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        })}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      if (!silent) {
        rastrearEventoFunil('aplicou_cupom', { ...snapshotFunil(), cupom: code, sucesso: false, motivo: 'erro' });
        tratarErro(error as AxiosError<ApiError>);
      }
    }
  };

  // Busca cupom de indicação do backend (vínculo seguro em tabela) e auto-aplica
  useEffect(() => {
    if (autoCupomTentado || !token) return;
    setAutoCupomTentado(true);
    const idEmpresa = obterIdTenent(token);
    if (!idEmpresa) return;
    api.get<{ codigoCupom: string; nomeIndicador: string }>(`/Afiliacao/CupomIndicacao/${idEmpresa}`)
      .then(res => {
        if (res.data?.codigoCupom) {
          setCouponCode(res.data.codigoCupom);
          if (res.data.nomeIndicador) {
            setIndicadorNome(res.data.nomeIndicador);
          }
          handleCouponApply(res.data.codigoCupom, true);
        }
      })
      .catch(() => { /* empresa não foi indicada — segue sem cupom */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCupomTentado, token]);

  // Mantém o desconto coerente quando a base muda (plano/ciclo/veículos/LocaSmartCheck)
  useEffect(() => {
    if (!cupomUsado || couponCode !== cupomUsado) return;
    handleCouponApply(cupomUsado, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precoTotalPlano, precoLocaSmartCheckCiclo, totalPrice]);

  const validateCardForm = () => {
    if (paymentMethod !== 'creditcard') return true;

    const errors = {
      cardName: '',
      cardNumber: '',
      expiration: '',
      cvv: '',
      cpf: '',
      postalCode: '',
      addressNumber: '',
      phone: ''
    };

    let isValid = true;
    const cardName = (document.getElementById('card-name') as HTMLInputElement)?.value.trim();
    const cardNumber = (document.getElementById('card-number') as HTMLInputElement)?.value.replace(/\s/g, '');
    const expiration = (document.getElementById('expiration') as HTMLInputElement)?.value;
    const cvv = (document.getElementById('cvv') as HTMLInputElement)?.value;
    const cpf = (document.getElementById('cpf') as HTMLInputElement)?.value.replace(/\D/g, '');
    const postalCode = (document.getElementById('postal-code') as HTMLInputElement)?.value.replace(/\D/g, '');
    const addressNumber = (document.getElementById('address-number') as HTMLInputElement)?.value.trim();
    const phone = (document.getElementById('phone') as HTMLInputElement)?.value.replace(/\D/g, '');

    if (!cardName || cardName.length < 3) {
      errors.cardName = 'Nome no cartão é obrigatório';
      isValid = false;
    }

    if (!cardNumber || cardNumber.length < 16 || !/^\d+$/.test(cardNumber)) {
      errors.cardNumber = 'Número inválido';
      isValid = false;
    }

    if (!expiration || !/^\d{2}\/\d{2}$/.test(expiration)) {
      errors.expiration = 'Inválido';
      isValid = false;
    }

    if (!cvv || !/^\d{3,4}$/.test(cvv)) {
      errors.cvv = 'Inválido';
      isValid = false;
    }

    if (!cpf || cpf.length !== 11) {
      errors.cpf = 'CPF Inválido';
      isValid = false;
    }

    if (!postalCode || postalCode.length !== 8) {
      errors.postalCode = 'CEP Inválido';
      isValid = false;
    }

    if (!addressNumber) {
      errors.addressNumber = 'Obrigatório';
      isValid = false;
    }

    if (!phone || phone.length < 10) {
      errors.phone = 'Inválido';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleCheckoutRequest = async () => {
    if (hasPendingInvoice) {
      toast.error('Você tem uma fatura pendente. Regularize seu pagamento antes de fazer alterações.');
      return;
    }

    if (activePaymentId) {
      toast.info('Aguarde a conclusão do pagamento em processamento.');
      return;
    }

    if (paymentMethod === 'creditcard' && !validateCardForm()) {
      return;
    }

    if (vehicleCount === currentVehicleLimit && tipoPlano === currentPlanType) {
      toast.info("Nenhuma alteração detectada.");
      return;
    }

    if (isExpansion) {
      if (!expansaoSimulacao) {
        toast.info("Aguarde o cálculo do valor da expansão.");
        return;
      }
      if (expansaoSimulacao.motivo) {
        toast.info(expansaoSimulacao.motivo);
        return;
      }
    }

    // Expansão de rastreio só é aceita no backend em grupos de 5; validar aqui evita
    // exibir um preço simulado e só falhar (500) na cobrança.
    if (isExpansion && expansaoSimulacao?.ehLocaSmartTrack) {
      const delta = vehicleCount - currentVehicleLimit;
      if (delta <= 0 || delta % 5 !== 0) {
        toast.info("A expansão de placas deve ser feita em grupos de 5.");
        return;
      }
    }

    rastrearEventoFunil('confirmou_checkout', snapshotFunil());
    setConfirmDialogOpen(true);
  };

  const handleConfirmCheckout = async () => {
    setConfirmDialogOpen(false);
    setIsSubmitting(true);
    setIsButtonsDisabled(true);

    const processingToast = toast.info('Iniciando processamento seguro...', {
      autoClose: false,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
      isLoading: true
    });

    try {
      if (estaAumentando) {
        await handleConfirmCheckoutAumentoLimite();
      } else {
        if (paymentMethod === 'pix') {
          await processAssinaturaPixPayment();
        } else {
          await processAssinaturaCreditCardPayment();
        }
      }
    } catch (error) {
      rastrearEventoFunil('checkout_falhou', { ...snapshotFunil(), motivo: 'erro_processamento' });
      enviarFilaFunil();
      tratarErro(error as AxiosError<ApiError>);
      toast.update(processingToast, {
        render: 'Erro ao processar',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
      setIsSubmitting(false);
      setIsButtonsDisabled(false);
      clearPaymentState();
    } finally {
      toast.dismiss(processingToast);
    }
  };

  const handleConfirmCheckoutAumentoLimite = async () => {
    if (acao === 'upgrade') {
      await processUpgradeDePlano();
    } else {
      if (paymentMethod === 'pix') {
        await processExpansaoLimitePixPayment();
      } else {
        await processExpansaoLimiteCreditCardPayment();
      }
    }
  };

  const processUpgradeDePlano = async () => {
    if (paymentMethod === 'pix') {
      await processUpgradePixPayment();
    } else {
      await processUpgradeCreditCardPayment();
    }
  }

  const processUpgradePixPayment = async () => {
    try {
      toast.info(`Gerando QR Code para upgrade...`);

      const response = await api.post('/api/payment/upgradePlano/pix', {
        QuantidadeDeVeiculos: vehicleCount,
        IdEmpresa: obterIdTenent(token),
        Valor: Number(finalPrice.toFixed(2)),
        CodigoCupom: cupomUsado,
        TipoPlano: tipoPlano,
        Ciclo: selectedPlanOption?.backendValue || 'MONTHLY'
      });

      if (!response.data.pedidoId) throw new Error('Falha ao gerar pedido de upgrade');

      const pixData = {
        qrCode: response.data.pixQrCode,
        qrCodeImage: response.data.pixQrCodeImageUrl,
        paymentUrl: response.data.paymentUrl,
        pixKey: response.data.pixKey
      };

      setPixData(pixData);
      savePaymentState({
        paymentMethod: 'pix',
        pedidoId: response.data.pedidoId,
        pixData,
        timestamp: Date.now(),
        status: 'processing'
      });

      toast.success('QR Code gerado! Aguardando pagamento...');
      await monitorPaymentStatus(response.data.pedidoId);

    } catch (error) {
      console.error('Erro no PIX de upgrade:', error);
      throw error;
    }
  };

  const processUpgradeCreditCardPayment = async () => {
    try {
      toast.info(`Processando upgrade no cartão...`);

      const cardName = (document.getElementById('card-name') as HTMLInputElement).value;
      const cardNumber = (document.getElementById('card-number') as HTMLInputElement).value.replace(/\s/g, '');
      const expiration = (document.getElementById('expiration') as HTMLInputElement).value;
      const [expiryMonth, expiryYear] = expiration.split('/');
      const ccv = (document.getElementById('cvv') as HTMLInputElement).value;
      const cpf = (document.getElementById('cpf') as HTMLInputElement).value.replace(/\D/g, '');
      const postalCode = (document.getElementById('postal-code') as HTMLInputElement).value.replace(/\D/g, '');
      const addressNumber = (document.getElementById('address-number') as HTMLInputElement).value;
      const phone = (document.getElementById('phone') as HTMLInputElement).value.replace(/\D/g, '');

      const response = await api.post('/api/payment/upgradePlano/credit-card', {
        QuantidadeDeVeiculos: vehicleCount,
        IdEmpresa: obterIdTenent(token),
        Valor: Number(finalPrice.toFixed(2)),
        CodigoCupom: cupomUsado,
        TipoPlano: tipoPlano,
        Ciclo: selectedPlanOption?.backendValue || 'MONTHLY',
        CreditCard: {
          holderName: cardName,
          number: cardNumber,
          expiryMonth,
          expiryYear: `20${expiryYear}`,
          ccv
        },
        CreditCardHolderInfo: {
          name: cardName,
          email: obterEmailUsuario(token),
          cpfCnpj: cpf,
          postalCode,
          addressNumber,
          phone
        }
      });

      const { Status, PedidoId } = response.data;

      if (Status === 'CONFIRMED' || Status === 'RECEIVED' || Status === 'UPGRADE_COMPLETED') {
        rastrearEventoFunil('checkout_concluido', snapshotFunil());
        enviarFilaFunil();
        toast.success('Upgrade realizado com sucesso! Atualizando sistema...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        handleLogout();
      } else {
        savePaymentState({
          paymentMethod: 'creditcard',
          pedidoId: PedidoId,
          timestamp: Date.now(),
          status: 'processing'
        });

        toast.info('Pagamento em processamento...');
        await monitorPaymentStatus(PedidoId);
      }

    } catch (error) {
      console.error('Erro no cartão de crédito para upgrade:', error);
      throw error;
    }
  };

  const processAssinaturaPixPayment = async () => {
    try {
      const planOption = PLAN_OPTIONS.find(p => p.id === selectedPlan);
      const ciclo = planOption?.backendValue || 'MONTHLY';

      // TODO: Reabilitar PIX Automático para mensal quando testado em produção
      const endpoint = '/api/payment/subscription/pix';

      const response = await api.post(endpoint, {
        QuantidadeDeVeiculos: vehicleCount,
        IdEmpresa: obterIdTenent(token),
        CodigoCupom: cupomUsado,
        Ciclo: ciclo,
        TipoPlano: tipoPlano,
        IncluirLocaSmartCheck: incluirLocaSmartCheck
      });

      if (!response.data.pedidoId) throw new Error('Pedido não criado');

      const pixData = {
        qrCode: response.data.pixQrCode,
        qrCodeImage: response.data.pixQrCodeImageUrl,
        paymentUrl: response.data.paymentUrl,
        pixKey: response.data.pixKey
      };

      setPixData(pixData);
      savePaymentState({
        paymentMethod: 'pix',
        pedidoId: response.data.pedidoId,
        pixData,
        timestamp: Date.now(),
        status: 'processing'
      });

      toast.success('QR Code gerado com sucesso!');
      await monitorPaymentStatus(response.data.pedidoId);
    } catch (error) {
      throw error;
    }
  };

  const processAssinaturaCreditCardPayment = async () => {
    try {
      const planOption = PLAN_OPTIONS.find(p => p.id === selectedPlan);
      const ciclo = planOption?.backendValue || 'MONTHLY';
      const cardName = (document.getElementById('card-name') as HTMLInputElement).value;
      const cardNumber = (document.getElementById('card-number') as HTMLInputElement).value.replace(/\s/g, '');
      const expiration = (document.getElementById('expiration') as HTMLInputElement).value;
      const [expiryMonth, expiryYear] = expiration.split('/');
      const ccv = (document.getElementById('cvv') as HTMLInputElement).value;
      const cpf = (document.getElementById('cpf') as HTMLInputElement).value.replace(/\D/g, '');
      const postalCode = (document.getElementById('postal-code') as HTMLInputElement).value.replace(/\D/g, '');
      const addressNumber = (document.getElementById('address-number') as HTMLInputElement).value;
      const phone = (document.getElementById('phone') as HTMLInputElement).value.replace(/\D/g, '');

      const response = await api.post('/api/payment/subscription/credit-card', {
        QuantidadeDeVeiculos: vehicleCount,
        IdEmpresa: obterIdTenent(token),
        CodigoCupom: cupomUsado,
        Ciclo: ciclo,
        TipoPlano: tipoPlano,
        IncluirLocaSmartCheck: incluirLocaSmartCheck,
        InstallmentCount: installmentCount > 1 ? installmentCount : undefined,
        CreditCard: {
          holderName: cardName,
          number: cardNumber,
          expiryMonth,
          expiryYear: `20${expiryYear}`,
          ccv
        },
        CreditCardHolderInfo: {
          name: cardName,
          email: obterEmailUsuario(token),
          cpfCnpj: cpf,
          postalCode,
          addressNumber,
          phone
        }
      });

      if (!response.data.pedidoId) throw new Error('Pedido não criado');

      savePaymentState({
        paymentMethod: 'creditcard',
        pedidoId: response.data.pedidoId,
        timestamp: Date.now(),
        status: 'processing'
      });

      await monitorPaymentStatus(response.data.pedidoId);
    } catch (error) {
      throw error;
    }
  }

  const processExpansaoLimitePixPayment = async () => {
    try {
      const response = await api.post('/api/payment/aumentoLimiteVeiculos/pix', {
        QuantidadeDeVeiculos: vehicleCount,
        IdEmpresa: obterIdTenent(token),
        Valor: Number(finalPrice.toFixed(2)),
        CodigoCupom: cupomUsado,
        TipoPlano: tipoPlano
      });

      if (!response.data.pedidoId) throw new Error('Pedido não criado');

      const pixData = {
        qrCode: response.data.pixQrCode,
        qrCodeImage: response.data.pixQrCodeImageUrl,
        paymentUrl: response.data.paymentUrl,
        pixKey: response.data.pixKey
      };

      setPixData(pixData);
      savePaymentState({
        paymentMethod: 'pix',
        pedidoId: response.data.pedidoId,
        pixData,
        timestamp: Date.now(),
        status: 'processing'
      });

      toast.success('QR Code PIX gerado com sucesso!');
      await monitorPaymentStatus(response.data.pedidoId);
    } catch (error) {
      throw error;
    }
  }

  const processExpansaoLimiteCreditCardPayment = async () => {
    try {
      const cardName = (document.getElementById('card-name') as HTMLInputElement).value;
      const cardNumber = (document.getElementById('card-number') as HTMLInputElement).value.replace(/\s/g, '');
      const expiration = (document.getElementById('expiration') as HTMLInputElement).value;
      const [expiryMonth, expiryYear] = expiration.split('/');
      const ccv = (document.getElementById('cvv') as HTMLInputElement).value;
      const cpf = (document.getElementById('cpf') as HTMLInputElement).value.replace(/\D/g, '');
      const postalCode = (document.getElementById('postal-code') as HTMLInputElement).value.replace(/\D/g, '');
      const addressNumber = (document.getElementById('address-number') as HTMLInputElement).value;
      const phone = (document.getElementById('phone') as HTMLInputElement).value.replace(/\D/g, '');

      const response = await api.post('/api/payment/aumentoLimiteVeiculos/credit-card', {
        QuantidadeDeVeiculos: vehicleCount,
        IdEmpresa: obterIdTenent(token),
        Valor: Number(finalPrice.toFixed(2)),
        CodigoCupom: cupomUsado,
        TipoPlano: tipoPlano,
        CreditCard: {
          holderName: cardName,
          number: cardNumber,
          expiryMonth,
          expiryYear: `20${expiryYear}`,
          ccv
        },
        CreditCardHolderInfo: {
          name: cardName,
          email: obterEmailUsuario(token),
          cpfCnpj: cpf,
          postalCode,
          addressNumber,
          phone
        }
      });

      if (!response.data.pedidoId) throw new Error('Pedido não criado');

      savePaymentState({
        paymentMethod: 'creditcard',
        pedidoId: response.data.pedidoId,
        timestamp: Date.now(),
        status: 'processing'
      });

      await monitorPaymentStatus(response.data.pedidoId);
    } catch (error) {
      throw error;
    }
  }

  const handleLogout = () => {
    clearPaymentState();
    dispatch({ type: 'LOG_OUT' });
    setTimeout(() => {
      navigate.push('/?toast=success');
    }, 500);
  };

  const handleBack = () => {
    navigate.push('/planos');
  };

  if (isCheckingPendingInvoice) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-10 w-10 text-[#1F467E] mx-auto" />
          <p className="mt-4 text-[#1F467E] font-medium">Verificando pagamentos...</p>
        </div>
      </div>
    );
  }

  // Confirmation dialog (shared between wizard and simplified flows)
  const renderConfirmDialog = () => {
    if (!confirmDialogOpen) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
          <div className="text-center pb-2 border-b border-gray-100">
            <h2 className="text-xl text-[#1F467E] font-bold">Confirmação de Pagamento</h2>
          </div>
          <div className="text-center py-2">
            <p className="text-gray-500 text-sm">Total a pagar agora:</p>
            <p className="text-3xl font-bold text-[#1F467E]">
              {finalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg text-sm text-[#1F467E]">
            <p>
              {paymentMethod === 'pix'
                ? estaAumentando ? 'Será gerado um QR Code para pagamento único de ajuste.' : 'Será gerado um QR Code para pagamento.'
                : estaAumentando ? 'Cobrança única no cartão para o ajuste proporcional.' : 'Cobrança no cartão de crédito.'}
            </p>
            {estaAumentando && (
              <p className="mt-2 font-semibold">
                * Sua assinatura será atualizada automaticamente e as próximas faturas virão com o novo valor.
              </p>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setConfirmDialogOpen(false)}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmCheckout}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-[#1F467E] hover:bg-blue-800 text-white rounded-lg font-bold shadow-md"
            >
              {isSubmitting ? 'Processando...' : 'Confirmar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const emailUsuario = obterEmailUsuario(token);

  // WIZARD FLOW — new subscriptions
  if (isWizardFlow) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <div className={`${currentStep === 3 ? 'max-w-[980px]' : 'max-w-[640px]'} mx-auto px-6 py-4 transition-all`}>
          <TopBar>
            <BackButton onClick={currentStep === 1 ? handleBack : handlePrevStep}>
              <FaArrowLeft /> {currentStep === 1 ? 'Voltar para Planos' : 'Voltar'}
            </BackButton>
          </TopBar>

          <WizardStepper currentStep={currentStep} totalSteps={3} />

          {currentStep === 1 && (
            <StepPlan
              tipoPlano={tipoPlano}
              vehicleCount={vehicleCount}
              setVehicleCount={aoAlterarVeiculos}
              selectedPlan={selectedPlan}
              setSelectedPlan={aoSelecionarCiclo}
              minVehicleLimit={effectiveMinLimit}
              calcularPrecoMensal={calcularPrecoMensalParaStep}
              onNext={handleNextStep}
            />
          )}

          {currentStep === 2 && (
            <StepAddons
              vehicleCount={vehicleCount}
              incluirLocaSmartCheck={incluirLocaSmartCheck}
              setIncluirLocaSmartCheck={aoAlterarLocaSmartCheck}
              selectedPlan={selectedPlan}
              onNext={handleAddonsNext}
              onSkip={handleAddonsSkip}
            />
          )}

          {currentStep === 3 && (
            <StepPayment
              paymentMethod={paymentMethod}
              setPaymentMethod={aoSelecionarPagamento}
              pixIndisponivel={pixIndisponivel}
              vehicleCount={vehicleCount}
              setVehicleCount={aoAlterarVeiculos}
              minVehicleLimit={effectiveMinLimit}
              selectedPlan={selectedPlan}
              setSelectedPlan={aoSelecionarCiclo}
              pixData={pixData}
              isSubmitting={isSubmitting || !!activePaymentId}
              isButtonsDisabled={isButtonsDisabled}
              hasActivePayment={!!activePaymentId}
              hasPendingInvoice={hasPendingInvoice}
              formErrors={formErrors}
              incluirLocaSmartCheck={incluirLocaSmartCheck}
              setIncluirLocaSmartCheck={aoAlterarLocaSmartCheck}
              isLocaSmartCheckAtivo={isLocaSmartCheckAtivo}
              precoLocaSmartCheckMensal={precoLocaSmartCheckMensal}
              planDiscount={planDiscount}
              currentVehicleLimit={currentVehicleLimit}
              basePrice={basePrice}
              totalPrice={totalPrice}
              discount={discount}
              finalPrice={finalPrice}
              couponCode={couponCode}
              setCouponCode={setCouponCode}
              onCouponApply={handleCouponApply}
              countdown={countdown}
              temPlanoAtivo={temPlanoAtivo}
              diasRestantes={diasRestantes}
              precoAtual={precoAtual}
              precoNovo={precoNovoMensal}
              precoMensalSemDesconto={precoNovoMensalSemDesconto}
              tipoPlano={tipoPlano}
              acao={acao}
              currentPlanType={currentPlanType}
              precoLocaSmartCheckCiclo={precoLocaSmartCheckCiclo}
              onCheckout={handleCheckoutRequest}
              installmentCount={installmentCount}
              setInstallmentCount={aoAlterarParcelamento}
              installmentOptions={installmentOptions}
              showInstallments={showInstallments}
              descontoOriginal={selectedPlanOption?.discount || 0}
              descontoAjustado={planDiscount}
              indicadorNome={indicadorNome}
            />
          )}

          <CheckoutAssistant step={currentStep} />
        </div>
        {renderConfirmDialog()}
        <ModalOnboarding
          open={showLocaSmartCheckOnboarding}
          onClose={handleLogout}
          email={emailUsuario}
          senha=""
        />
      </div>
    );
  }

  // Expansão sem plano pago vigente (vencido, trial ou gratuito): antes caía no fluxo de assinatura NOVA mensal,
  // que cancelava a assinatura vigente no Asaas e cobrava um mês cheio. O servidor também recusa.
  if (isExpansion && !temPlanoAtivo) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <div className="max-w-[640px] mx-auto px-6 py-9">
          <TopBar>
            <BackButton onClick={handleBack}>
              <FaArrowLeft /> Voltar para Planos
            </BackButton>
          </TopBar>
          <div className="bg-white rounded-xl border border-blue-100 shadow-md p-6 text-center">
            <h1 className="text-[22px] font-extrabold text-[#1F467E] mb-2">Expandir Frota</h1>
            <p className="text-[15px] text-gray-600 mb-4">
              {planoExpirado
                ? 'Seu plano está com a fatura vencida. Regularize o pagamento para expandir a frota; as placas novas valem pelo restante do período pago.'
                : 'A expansão de placas é para planos pagos. Contrate um plano para aumentar o limite de veículos.'}
            </p>
            {hasPendingInvoice && pixData?.paymentUrl && (
              <a href={pixData.paymentUrl} target="_blank" rel="noreferrer"
                className="inline-block px-5 py-2.5 bg-[#1F467E] text-white rounded-lg font-bold hover:bg-[#162f56] mb-3">
                Pagar fatura em aberto
              </a>
            )}
            <div>
              <button onClick={handleBack} className="px-5 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50">
                Ver planos
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SIMPLIFIED FLOW — expansion/upgrade
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div className="max-w-[1100px] mx-auto px-6 py-9">
        <TopBar>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Voltar para Planos
          </BackButton>
        </TopBar>

        <div className="text-center mb-8">
          <h1 className="text-[26px] font-extrabold text-[#1F467E] mb-1.5">
            {isUpgrade ? 'Upgrade de Plano' : 'Expandir Frota'}
          </h1>
          <p className="text-[15px] text-[#94a3b8]">
            {isUpgrade ? 'Atualize para o plano Avançado' : 'Aumente o limite de veículos'}
          </p>
        </div>

        {isExpansion && expansaoSimulacao?.ehLocaSmartTrack && (
          <p style={{ fontSize: '0.85rem', color: '#0f766e' }}>
            Valor por placa do seu plano: R$ {expansaoSimulacao.valorPorPlacaMensal.toFixed(2)}/mês
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <CheckoutSummary
            vehicleCount={vehicleCount}
            currentVehicleLimit={currentVehicleLimit}
            basePrice={basePrice}
            totalPrice={totalPrice}
            discount={discount}
            finalPrice={finalPrice}
            onCheckout={handleCheckoutRequest}
            paymentMethod={paymentMethod}
            selectedPlan={selectedPlan}
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            onCouponApply={handleCouponApply}
            isButtonsDisabled={isButtonsDisabled || isSubmitting || !!activePaymentId || hasPendingInvoice}
            countdown={countdown}
            temPlanoAtivo={temPlanoAtivo}
            diasRestantes={diasRestantes}
            precoAtual={precoAtual}
            precoNovo={precoNovoMensal}
            precoMensalSemDesconto={precoNovoMensalSemDesconto}
            hasActivePayment={!!activePaymentId}
            tipoPlano={tipoPlano}
            acao={acao}
            currentPlanType={currentPlanType}
            incluirLocaSmartCheck={incluirLocaSmartCheck || isLocaSmartCheckAtivo}
            precoLocaSmartCheck={precoLocaSmartCheckCiclo}
            onRemoveLocaSmartCheck={!isLocaSmartCheckAtivo ? () => setIncluirLocaSmartCheck(false) : undefined}
            descontoPercentual={planDiscount}
            precoLocaSmartCheckMensal={precoLocaSmartCheckMensal}
            precoLocaSmartCheckMensalAtual={precoLocaSmartCheckMensalAtual}
            indicadorNome={indicadorNome}
            valorAtualMensalServidor={mensaisServidor?.atual}
            valorNovoMensalServidor={mensaisServidor?.novo}
            expansao={isExpansion ? expansaoSimulacao : null}
          />

          <CheckoutForm
            vehicleCount={vehicleCount}
            setVehicleCount={setVehicleCount}
            paymentMethod={paymentMethod}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            pixData={paymentMethod === 'pix' ? pixData : undefined}
            isSubmitting={isSubmitting || !!activePaymentId}
            onCheckout={handleCheckoutRequest}
            formErrors={formErrors}
            hasActivePayment={!!activePaymentId}
            hasPendingInvoice={hasPendingInvoice}
            isExpansion={isExpansion}
            isUpgrade={isUpgrade}
            minVehicleLimit={effectiveMinLimit}
            incluirLocaSmartCheck={incluirLocaSmartCheck}
            setIncluirLocaSmartCheck={setIncluirLocaSmartCheck}
            isLocaSmartCheckAtivo={isLocaSmartCheckAtivo}
            precoLocaSmartCheckMensal={precoLocaSmartCheckMensal}
            planDiscount={planDiscount}
          />
        </div>

        <CheckoutAssistant step={1} />
      </div>
      {renderConfirmDialog()}
      <ModalOnboarding
        open={showLocaSmartCheckOnboarding}
        onClose={handleLogout}
        email={emailUsuario}
        senha=""
      />
    </div>
  );
};

export default CheckoutPage;